import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';

export interface UserWallet {
  id: number;
  user_id: number;
  currency_code: string;
  balance: number | string;
  is_active: boolean;
  currency?: {
    id: number;
    code: string;
    rate: number;
    created_at: string;
    updated_at: string;
  };
  created_at: string;
  updated_at: string;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  name?: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  is_verified: boolean;
  country?: any;
  state?: any;
  city?: any;
  street?: string | null;
  house_no?: string | null;
  zip_code?: string | null;
  phone?: string | null;
  use_for?: string | null;
  language?: string | null;
  company?: string | null;
  role?: string | null;
  default_currency_code?: string;
  wallets?: UserWallet[];
  total_wallet_balance?: number;
  created_at: string;
  updated_at: string;
}

export interface UserStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  admins: number;
  verified_users: number;
}

export interface UserFilters {
  search?: string;
  country_id?: number;
  is_active?: boolean | string;
  is_admin?: boolean | string;
  page?: number;
  per_page?: number;
}

export interface PaginationMeta {
  current_page: number;
  from: number;
  last_page: number;
  links: Array<{url: string | null, label: string, active: boolean}>;
  path: string;
  per_page: number;
  to: number;
  total: number;
}

export interface ApiResponse<T> {
  data: T;
  status: string;
  links?: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta?: PaginationMeta;
}

// Function to get users with filters
const getUsers = async (filters: UserFilters = {}): Promise<ApiResponse<User[]>> => {
  const response = await api.get('/admin/users', { params: filters });
  return response.data;
};

// Function to get a single user
const getUser = async (id: number): Promise<ApiResponse<User>> => {
  const response = await api.get(`/admin/users/${id}`);
  return response.data;
};

// Function to get user statistics
const getUserStats = async (): Promise<ApiResponse<UserStats>> => {
  const response = await api.get('/admin/users/stats');
  return response.data;
};

// Function to update a user
const updateUser = async ({ id, data }: { id: number, data: Partial<User> }): Promise<ApiResponse<User>> => {
  const response = await api.put(`/admin/users/${id}`, data);
  return response.data;
};

// Function to create a new user
const createUser = async (data: any): Promise<ApiResponse<User>> => {
  const response = await api.post('/admin/users', data);
  return response.data;
};

// Function to delete a user
const deleteUser = async (id: number): Promise<ApiResponse<any>> => {
  const response = await api.delete(`/admin/users/${id}`);
  return response.data;
};

// Function to toggle admin status
const toggleAdmin = async (id: number): Promise<ApiResponse<User>> => {
  const response = await api.put(`/admin/users/${id}/toggle-admin`);
  return response.data;
};

export const useAdminUsers = (filters: UserFilters = {}) => {
  const queryClient = useQueryClient();
  
  // Query to get users list
  const { 
    data: response,
    isLoading,
    error,
    refetch
  } = useQuery<ApiResponse<User[]>>({
    queryKey: ['admin', 'users', filters],
    queryFn: () => getUsers(filters),
  });
  
  // Extract data and meta
  const users = response?.data || [];
  const pagination = response?.meta;
  const totalUsers = pagination?.total || 0;
  
  // Query to get user stats
  const {
    data: statsResponse,
    isLoading: isLoadingStats
  } = useQuery<ApiResponse<UserStats>>({
    queryKey: ['admin', 'users', 'stats'],
    queryFn: getUserStats,
  });
  
  const stats = statsResponse?.data || {
    total_users: 0,
    active_users: 0,
    inactive_users: 0,
    admins: 0,
    verified_users: 0
  };
  
  // Mutation to update user
  const updateUserMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', data.data.id] });
    }
  });
  
  // Mutation to create user
  const createUserMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'stats'] });
    }
  });
  
  // Mutation to delete user
  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'stats'] });
    }
  });
  
  // Mutation to toggle admin status
  const toggleAdminMutation = useMutation({
    mutationFn: toggleAdmin,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', data.data.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'stats'] });
    }
  });
  
  // Mutation to toggle active status
  const toggleActiveStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number, isActive: boolean }) => 
      updateUser({ id, data: { is_active: !isActive }}),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', data.data.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'stats'] });
    }
  });
  
  return {
    users,
    pagination,
    totalUsers,
    stats,
    isLoading,
    isLoadingStats,
    error,
    refetch,
    updateUser: (id: number, data: Partial<User>) => updateUserMutation.mutateAsync({ id, data }),
    createUser: (data: any) => createUserMutation.mutateAsync(data),
    deleteUser: (id: number) => deleteUserMutation.mutateAsync(id),
    toggleAdmin: (id: number) => toggleAdminMutation.mutateAsync(id),
    toggleActiveStatus: (id: number, isActive: boolean) => toggleActiveStatusMutation.mutateAsync({ id, isActive }),
    isUpdating: updateUserMutation.isPending,
    isCreating: createUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending,
    isTogglingAdmin: toggleAdminMutation.isPending,
    isTogglingActive: toggleActiveStatusMutation.isPending
  };
};

// Custom hook to get a single user
export const useAdminUser = (id: number) => {
  const queryClient = useQueryClient();
  
  const { 
    data: response,
    isLoading,
    error
  } = useQuery<ApiResponse<User>>({
    queryKey: ['admin', 'user', id],
    queryFn: () => getUser(id),
    enabled: !!id
  });
  
  const user = response?.data;
  
  // Mutation to update user
  const updateUserMutation = useMutation({
    mutationFn: (data: Partial<User>) => updateUser({ id, data }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'user', id] });
    }
  });
  
  return {
    user,
    isLoading,
    error,
    updateUser: (data: Partial<User>) => updateUserMutation.mutateAsync(data),
    isUpdating: updateUserMutation.isPending
  };
}; 
